import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Rating } from "primereact/rating";
import { Slider } from "primereact/slider";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { getAddressFromCoordinates, getCurrentLocation } from "../../../utils/stringUtils";
import { buildGoogleMapsSearchUrl, openUrlInCurrentTab, openUrlInNewTab } from "../../../utils/securityUtils";
import { AsyncProfileImageInDataTableRow } from "../../home/components/AsyncProfileImageInDataTableRow";
import SearchAddress from "../../../components/general/SearchAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

const DashboardFindCaregiver = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [matchedUsers, setMatchedUsers] = useState<any[]>([]);

  const [showProfileDialog, setShowProfileDialog] = useState<boolean>(false);
  const [selectedCaregiverProfile, setSelectedCaregiverProfile] = useState<any>(null);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
  const [radiusInMiles, setRadiusInMiles] = useState<number>(20);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);

  const defaultValues = {
    latitude: 0,
    longitude: 0,
    address: '',
    radiusInMiles: 20
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  useEffect(() => {
    const fetchCaregiverDetails = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const location = await getCurrentLocation();
        const address = await getAddressFromCoordinates(location.latitude, location.longitude);
        setSelectedAddress(address!);
        reset({
          ...defaultValues,
          latitude: location.latitude,
          longitude: location.longitude,
        });
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoading(false);
      }
    };
    fetchCaregiverDetails();
  }, []);

  const handleAddressChange = (event: any) => {
    if (!event || !event.target || !event.target.value || event.target.value === "") {
      reset();
      setSelectedAddress("");
      return;
    }

    reset({
      ...defaultValues,
      latitude: event.target.latitude,
      longitude: event.target.longitude
    });
    setSelectedAddress(event.target.value);
  };

  const onSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);
    setMatchedUsers([]);
    setLastEvaluatedKey(null);

    if (!data.latitude || !data.longitude) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(), detail: 'Please select a valid address from the suggestions.', life: 3000 });
      setLoading(false);
      return;
    }

    try {
      const response = await backendAPIStore.searchCaregiverJobProfiles({
        latitude: data.latitude,
        longitude: data.longitude,
        radiusInMiles: data.radiusInMiles,
        limit: 10,
        lastEvaluatedKey: lastEvaluatedKey
      });
      setMatchedUsers(response.items || []);
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
      setLatitude(data.latitude);
      setLongitude(data.longitude);
      setRadiusInMiles(data.radiusInMiles);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to search caregivers', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async (e) => {
    e.preventDefault();
    setLoadMoreLoading(true);

    try {
      const response = await backendAPIStore.searchCaregiverJobProfiles({
        latitude: latitude,
        longitude: longitude,
        radiusInMiles: radiusInMiles,
        limit: 10,
        lastEvaluatedKey: lastEvaluatedKey
      });
      setMatchedUsers(prevUsers => [...prevUsers, ...(response.items || [])]);
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to load more results', life: 3000 });
    } finally {
      setLoadMoreLoading(false);
    }
  };

  const onRequestDocuments = async (caregiverDetails) => {
    var requestBody = {
      targetUserId: caregiverDetails.userId,
      eventType: "REQUEST_CAREGIVER_DOCUMENTS",
    }

    confirmDialog({
      message: 'To request documents from this user, system will send an email notification to the user. Do you want to proceed? ' +
        ' You will receive the email once the user uploads and share with you in the system.',
      header: 'Confirm Request',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-warning p-button-outlined',
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.sendNotification(requestBody);
          toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Document request sent successfully', life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to send document request', life: 3000 });
        }
      }
    });
  };

  const footerTemplate = (rowData) => {
    {
      return (
        <div className="flex justify-content-between align-items-center grid">
          <div className="col flex justify-content-center">
            <Button label="View Details" className="mt-2 p-button-outlined" onClick={(e) => {
              e.preventDefault();
              setSelectedCaregiverProfile(rowData);
              setShowProfileDialog(true);
            }} severity="warning" rounded />
            <Button label="Request documents" className="mt-2 p-button-outlined" onClick={(e) => {
              e.preventDefault();
              onRequestDocuments(rowData.userDetail);
            }} severity="warning" rounded />
          </div >
        </div >
      );
    }
  };

  const resultTableSummaryTemplate = (rowData) => {
    return (
      <Card title={titleTemplate(`${rowData.userDetail.firstName} ${rowData.userDetail.lastName}`)}
        subTitle={`${rowData.jobTitle || "N/A"}`} className="p-card-shadow justify-content-center align-items-center flex"
        footer={footerTemplate(rowData)}>
        <div className="justify-content-center align-items-center w-100">
          <AsyncProfileImageInDataTableRow userId={rowData.userDetail.userId} />
          <div><strong>Wage: </strong>${rowData.expectedSalaryRange || "N/A"}/hr</div>
          <div><strong>City: </strong>{rowData.userDetail.city || "N/A"}</div>
        </div >
        <div>
          <div><strong>Day Shift Availability: </strong>{rowData.availableDayShifts.map((shift) => shift.name).join(", ") || "N/A"}</div>
          <div><strong>Night Shift Availability: </strong>{rowData.availableNightShifts.map((shift) => shift.name).join(", ") || "N/A"}</div>
        </div>
        <div>
          <small><strong>Contact: </strong></small>
          <i className="pi pi-map-marker" style={{ cursor: 'pointer', marginRight: '1em' }} onClick={(e) => {
            e.preventDefault();
            const mapsUrl = buildGoogleMapsSearchUrl(rowData.userDetail.latitude, rowData.userDetail.longitude);
            if (mapsUrl) {
              openUrlInNewTab(mapsUrl);
            }
          }}></i>
          <i className="pi pi-phone" style={{ cursor: 'pointer', marginRight: '1em' }} onClick={(e) => {
            e.preventDefault();
            openUrlInCurrentTab(`tel:${rowData.userDetail.phoneNumber}`);
          }}></i>
          <i className="pi pi-envelope" style={{ cursor: 'pointer' }} onClick={(e) => {
            e.preventDefault();
            openUrlInCurrentTab(`mailto:${rowData.userDetail.email}`);
          }}></i>
        </div>
      </Card >
    );
  }

  const handleReset = (e) => {
    e.preventDefault();
    reset(defaultValues);
    setSelectedAddress("");
    setMatchedUsers([]);
    setLastEvaluatedKey(null);
  }

  const titleTemplate = (title) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>{title}</span>
      </div>
    );
  };

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }} className="p-component">
      <Toast ref={toast}></Toast>
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          "Find Caregivers",
          "Search nearby caregivers who are actively looking for work and review their profiles.",
          <div style={{ ...dashboardSectionCardStyle, padding: "0.75rem 1rem", boxShadow: "none" }}>
            Results: <strong className="ml-1">{matchedUsers.length}</strong>
          </div>,
        )}

        <div style={dashboardSectionCardStyle}>
          <div style={{ ...appAccentPanelStyle, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
            <div className="flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}>Caregiver Search Workspace</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  Search nearby caregivers, review profile summaries, and request documents from one dashboard surface.
                </div>
              </div>
              <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 1rem" }}>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Search Radius</div>
                <div className="mt-2" style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a" }}>{radiusInMiles} miles</div>
              </div>
            </div>
          </div>

          <div style={{ padding: "1.25rem" }}>
          <form>
            <div>
              <div className="grid dashboard-search-grid">
                <div className="field col-12 lg:col-8 d-flex flex-column dashboard-search-location-field">
                  <label>Current Location</label>
                  <span className="p-float-label">
                    <SearchAddress name="address" id="address" onChange={handleAddressChange} required value={selectedAddress} />
                  </span>
                  {getFormErrorMessage('address')}
                </div>

                <div className="field col-12 lg:col-4 d-flex flex-column dashboard-search-radius-field">
                  <label>Search Radius (in miles)</label>
                  <span className="p-float-label">
                    <Controller
                      name="radiusInMiles"
                      control={control}
                      rules={{ required: "Search radius is required" }}
                      render={({ field }) => (
                        <div className="dashboard-radius-control">
                          <div className="mb-2 dashboard-radius-value">{field.value} miles</div>
                          <Slider value={field.value} onChange={(e) => field.onChange(e.value)} className="dashboard-radius-slider" style={{ cursor: "pointer" }}
                            min={10} max={50} step={10} />
                        </div>
                      )}
                    />
                  </span>
                  {getFormErrorMessage('radiusInMiles')}
                </div>
                <Divider type="dotted" />
                <div className="field col-12 d-flex flex-wrap justify-content-end gap-2">
                  <Button label="Search" className=" p-button-outlined" onClick={handleSubmit(onSubmit)} severity="warning" rounded />
                  <Button label="Reset" className="p-button-outlined" onClick={handleReset} severity="contrast" rounded />
                </div>
                <div className="p-error">{errorMessage}</div>
              </div>
            </div>
            {/* <div>
            {
              matchedUsers.length > 0 && (<div className="col">
                <b>{`Found ${matchedUsers.length} users within ${radiusInMiles} miles of the selected location.`}</b>
              </div>
              )
            }
          </div> */}

            <div className="col-12 mt-2">
              {loading && (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                  <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                </div>
              )}
              {!loading && matchedUsers.length > 0 ? (
                <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem", padding: "0.9rem 1rem" }}>
                  <strong>{matchedUsers.length}</strong> caregivers found within <strong>{radiusInMiles} miles</strong> of the selected location.
                </div>
              ) : null}
              <div className="grid">
                {
                  matchedUsers.map((user) => (
                    <div key={user.userDetail.userId} className="col-12 md:col-6 lg:col-4">
                      {resultTableSummaryTemplate(user)}
                    </div>))
                }
              </div>
              {
                lastEvaluatedKey && (
                  <div className="justify-content-center align-items-center flex">
                    <Button label="Load More" className="m-2 p-button-outlined " onClick={handleLoadMore} severity="info" rounded loading={loadMoreLoading} />
                  </div>
                )
              }
            </div>
          </form>
          </div>
        </div>
      </div>

      <Dialog
        header={renderSurfaceHeader("Caregiver Profile", "Review caregiver background, availability, and work history.", "pi pi-user")}
        visible={showProfileDialog}
        modal
        style={appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowProfileDialog(false)}
      >
        <div>
          {selectedCaregiverProfile ? (
            <div>
              <div style={appFormSectionStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Contact Details</div>
                <div className="grid mt-2" style={appMutedTextStyle}>
                  <div className="col-12 md:col-6"><strong>Name:</strong> {selectedCaregiverProfile.userDetail.firstName} {selectedCaregiverProfile.userDetail.lastName}</div>
                  <div className="col-12 md:col-6"><strong>Contact:</strong> {selectedCaregiverProfile.userDetail.phoneNumber || "N/A"}</div>
                  <div className="col-12 md:col-6"><strong>Email:</strong> {selectedCaregiverProfile.userDetail.email || "N/A"}</div>
                  <div className="col-12 md:col-6"><strong>Address:</strong> {selectedCaregiverProfile.userDetail.formattedAddress || "N/A"}</div>
                </div>
              </div>

              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Professional Details</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Job Title:</strong> {selectedCaregiverProfile.jobTitle || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Job Description:</strong> {selectedCaregiverProfile.jobDescription || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Education Level:</strong> {selectedCaregiverProfile.levelOfEducation.name || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Skills:</strong> {selectedCaregiverProfile.skills.map((skill) => skill.name).join(", ") || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Certifications:</strong> {selectedCaregiverProfile.certifications.map((cert) => cert.name).join(", ") || "N/A"}</div>
              </div>

              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Availability And Compensation</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Available From:</strong> {new Date(selectedCaregiverProfile.joiningDate).toLocaleDateString()}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Day Shift Availability:</strong> {selectedCaregiverProfile.availableDayShifts.map((shift) => shift.name).join(", ") || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Night Shift Availability:</strong> {selectedCaregiverProfile.availableNightShifts.map((shift) => shift.name).join(", ") || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Willingness to relocate:</strong> {selectedCaregiverProfile.relocationWillingness ? "Yes" : "No"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Require living arrangements:</strong> {selectedCaregiverProfile.livingArrangements ? "Yes" : "No"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Experience:</strong> {selectedCaregiverProfile.yearsOfExperience} years</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Previous Employer 1:</strong> {selectedCaregiverProfile.previousEmployerFirst || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Previous Employer 2:</strong> {selectedCaregiverProfile.previousEmployerSecond || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Previous Employer 3:</strong> {selectedCaregiverProfile.previousEmployerThird || "N/A"}</div>
              </div>

              <Divider>
                <div className="inline-flex align-items-center">
                  <i className="pi pi-star mr-2"></i>
                  <b>Reviews ({selectedCaregiverProfile.reviews ? selectedCaregiverProfile.reviews.length : 0})</b>
                </div>
              </Divider>
              {selectedCaregiverProfile.reviews && selectedCaregiverProfile.reviews.length > 0 ? (
                selectedCaregiverProfile.reviews.map((review) => (
                  <div key={review.reviewId} style={{ marginBottom: "1em" }}>
                    <p>
                      <Rating value={review.reviewStar} readOnly cancel={false} />
                    </p>
                    <p style={{ whiteSpace: "pre-wrap" }}><strong>Comment:</strong> {review.reviewComment || "N/A"}</p>
                    <p><strong>Created At:</strong> {new Date(review.createdAt).toLocaleDateString()}</p>
                    <Divider type="solid"></Divider>
                  </div>
                ))
              ) : (
                <p>No reviews available.</p>
              )}
            </div>
          ) : (
            <p>No profile selected.</p>
          )}
        </div>
      </Dialog>

    </div>
  );
};

export default DashboardFindCaregiver;
